import { useGetList, useNotify, useUpdate } from "ra-core";
import { Link } from "react-router";
import {
  AlertTriangle,
  ArrowUpRight,
  Flame,
  MessageSquare,
  Send,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BulkGhostEnrichButton,
  CopyHookButton,
  JobChangeBadge,
  LeadScoreBadge,
  OutreachStageSelect,
} from "../contacts/LinkedInCrmWidgets";
import type { Contact } from "../types";

export const LinkedInCommandCenter = () => {
  const { data: contacts = [], isPending } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "last_seen", order: "DESC" },
  });
  const [update] = useUpdate<Contact>();
  const notify = useNotify();

  if (isPending || !contacts.length) return null;

  const jobChanges = contacts.filter((c) => c.job_change_flagged);
  const hookReady = contacts.filter(
    (c) =>
      (c.outreach_stage === "hook_ready" || c.outreach_stage === "new") &&
      c.ai_icebreaker,
  );
  const activeThreads = contacts.filter(
    (c) =>
      c.outreach_stage === "dm_sent" ||
      c.outreach_stage === "replied" ||
      c.outreach_stage === "meeting_booked",
  );
  const scoredContacts = contacts.filter((c) => (c.lead_score ?? 0) > 0);
  const avgScore = scoredContacts.length
    ? Math.round(
        scoredContacts.reduce((acc, c) => acc + (c.lead_score ?? 0), 0) /
          scoredContacts.length,
      )
    : 0;

  const handleSendDmAndAdvance = async (contact: Contact) => {
    if (contact.ai_icebreaker) {
      await navigator.clipboard.writeText(contact.ai_icebreaker).catch(() => {});
    }
    if (contact.linkedin_url) {
      window.open(contact.linkedin_url, "_blank", "noopener,noreferrer");
    }
    update(
      "contacts",
      {
        id: contact.id,
        data: {
          outreach_stage: "dm_sent",
          last_seen: new Date().toISOString(),
        },
        previousData: contact,
      },
      { mutationMode: "optimistic" },
    );
    notify(
      `Hook copied & LinkedIn opened for ${contact.first_name} (Stage → DM Sent)`,
      { type: "info" },
    );
  };

  return (
    <div className="mb-6 space-y-4">
      {/* Top Command Banner */}
      <div className="rounded-xl border border-indigo-500/25 bg-gradient-to-r from-indigo-500/10 via-background to-sky-500/10 p-4 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
              <Sparkles className="size-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold tracking-tight text-foreground">
                  AI LinkedIn Outbound & Signal Command Center
                </h2>
                <span className="rounded-full bg-indigo-500/15 px-2 py-0.5 text-[11px] font-semibold text-indigo-600 dark:text-indigo-300">
                  Gemini 2.5 + Serper Live
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Real-time LinkedIn DOM clipping, automated job-change detection, and 1-click personalized DM hooks.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <BulkGhostEnrichButton contacts={contacts} />
            <Link to="/contacts">
              <Button type="button" size="sm" variant="default" className="cursor-pointer gap-1.5">
                <span>Open Clay AI Table</span>
                <ArrowUpRight className="size-3.5" />
              </Button>
            </Link>
          </div>
        </div>

        {/* 4 Signal KPI Strip */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg border bg-background/80 p-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Enriched Prospects</span>
              <Users className="size-3.5 text-indigo-500" />
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums">
                {contacts.length}
              </span>
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                Avg ICP Score: {avgScore}/100
              </span>
            </div>
          </div>

          <div className="rounded-lg border bg-background/80 p-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Job Change Signals</span>
              <AlertTriangle className="size-3.5 text-rose-500" />
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums text-rose-600 dark:text-rose-400">
                {jobChanges.length}
              </span>
              <span className="text-xs text-muted-foreground">
                Champion moves detected
              </span>
            </div>
          </div>

          <div className="rounded-lg border bg-background/80 p-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Hooks Ready to Send</span>
              <Target className="size-3.5 text-indigo-500" />
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums text-indigo-600 dark:text-indigo-400">
                {hookReady.length}
              </span>
              <span className="text-xs text-muted-foreground">
                AI icebreakers queued
              </span>
            </div>
          </div>

          <div className="rounded-lg border bg-background/80 p-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Active DM Threads</span>
              <MessageSquare className="size-3.5 text-sky-500" />
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums text-sky-600 dark:text-sky-400">
                {activeThreads.length}
              </span>
              <span className="text-xs text-muted-foreground">
                Sent / Replied / Booked
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 2-Column Signal & Outreach Action Queues */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Priority AI Outbound Queue */}
        <Card className="py-3">
          <CardHeader className="pb-2 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Flame className="size-4 text-emerald-500" />
                <span>Priority Outbound Queue (1-Click LinkedIn DM)</span>
              </CardTitle>
              <Link
                to="/contacts"
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                View all
              </Link>
            </div>
          </CardHeader>
          <CardContent className="px-4 space-y-2.5">
            {hookReady.slice(0, 3).map((contact) => (
              <div
                key={contact.id}
                className="rounded-lg border bg-muted/25 p-3 hover:bg-muted/40 transition-colors"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/contacts/${contact.id}/show`}
                      className="font-semibold text-sm hover:underline"
                    >
                      {contact.first_name} {contact.last_name}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      · {contact.detected_company || contact.company_name}
                    </span>
                    <LeadScoreBadge contact={contact} />
                  </div>
                  <OutreachStageSelect contact={contact} />
                </div>

                {contact.ai_icebreaker && (
                  <p className="mt-1.5 text-xs text-muted-foreground italic line-clamp-2">
                    "{contact.ai_icebreaker}"
                  </p>
                )}

                <div className="mt-2 flex items-center justify-end gap-1.5">
                  {contact.ai_icebreaker && (
                    <CopyHookButton
                      icebreaker={contact.ai_icebreaker}
                      size="sm"
                      variant="outline"
                    />
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    className="cursor-pointer gap-1.5 h-7 text-xs"
                    onClick={() => handleSendDmAndAdvance(contact)}
                  >
                    <Send className="size-3" />
                    <span>Copy & DM on LinkedIn</span>
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Champion Job Change Radar */}
        <Card className="py-3">
          <CardHeader className="pb-2 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="size-4 text-rose-500" />
                <span>Champion Job-Change Radar (Serper Signals)</span>
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                Auto-flagged when LinkedIn ≠ CRM company
              </span>
            </div>
          </CardHeader>
          <CardContent className="px-4 space-y-2.5">
            {jobChanges.length > 0 ? (
              jobChanges.slice(0, 3).map((contact) => (
                <div
                  key={contact.id}
                  className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/contacts/${contact.id}/show`}
                        className="font-semibold text-sm hover:underline"
                      >
                        {contact.first_name} {contact.last_name}
                      </Link>
                      <JobChangeBadge contact={contact} />
                    </div>
                    <LeadScoreBadge contact={contact} />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Moved from{" "}
                    <span className="font-medium text-foreground">
                      {contact.company_name || "Previous Company"}
                    </span>{" "}
                    →{" "}
                    <span className="font-semibold text-rose-600 dark:text-rose-400">
                      {contact.detected_company}
                    </span>{" "}
                    ({contact.title})
                  </div>
                  {contact.recent_post_summary && (
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      📌 {contact.recent_post_summary}
                    </p>
                  )}
                  <div className="mt-2 flex items-center justify-end gap-1.5">
                    {contact.ai_icebreaker && (
                      <CopyHookButton
                        icebreaker={contact.ai_icebreaker}
                        size="sm"
                        variant="outline"
                        label="Copy Congrats Hook"
                      />
                    )}
                    <Link to={`/contacts/${contact.id}/show`}>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-7 text-xs cursor-pointer"
                      >
                        Open Dossier
                      </Button>
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-6 text-center text-xs text-muted-foreground">
                No job changes flagged yet. Run Ghost Enrich to scan LinkedIn.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
